import { refreshMonetization } from '@/features/credits';
import submitWebHook from '@/requests/magic-flow/submitWebHook';
import pollUntilCompleted from '@/requests/magic-flow/pollUntilCompleted';
import { MAGIC_FLOW_WEBHOOK_URL } from '@/consts/urls';
import buildParamsFromInputs from '@/utils/flow/buildParamsFromInputs';
import createNotificationConfig from '@/utils/ui/createNotificationConfig';
import { saveGeneration } from '@/utils/storage/generationStorage';
import buildFlowMediaList from '@/utils/generation/buildFlowMediaList';

import type { ContentFileAttributes, IInput, Preset } from '@/types';
import type { BuiltFlowMedia } from '@/utils/generation/buildFlowMediaList';

export interface SubmitAndPollResult {
  generationId: string;
  /** Every result the run returned, in the order the backend listed them. */
  built: BuiltFlowMedia[];
}

/**
 * Submits the webhook, persists the generation for crash recovery, then
 * polls until completion and parses the results into `FlowMedia` shapes.
 *
 * Uses the same `pollUntilCompleted` + `buildFlowMediaList` pair that the main
 * `useGenerate` polling pipeline uses, so both flows share one source of
 * truth for result parsing — including a flow that answers with several
 * outputs, which this path used to reduce to `result[0]`.
 * `buildFlowMediaList` throws when the poll response carries no result URL —
 * caller treats that as a generation failure.
 */
const submitAndPollGeneration = async ({
  webhookId,
  selectedPreset,
  inputs,
  toolId,
  folderName,
  placeholderUuid,
  startedAt,
  attributes,
}: {
  webhookId: string;
  selectedPreset: Preset;
  inputs: IInput[];
  toolId: string;
  folderName: string;
  placeholderUuid: string;
  startedAt: number;
  attributes?: ContentFileAttributes;
}): Promise<SubmitAndPollResult> => {
  // Build params and submit webhook
  const webhookParams = buildParamsFromInputs(inputs);
  const webhookResponse = await submitWebHook({
    webhookId,
    params: webhookParams,
    driveOptions: {
      folderName,
      packageId: toolId,
      attributes,
    },
    notificationConfig: createNotificationConfig(),
  });
  const generationId = webhookResponse.id;

  refreshMonetization();

  await saveGeneration({
    generationId,
    placeholderUuid,
    presetTitle: selectedPreset.title || '',
    startedAt,
    presetType: selectedPreset.type,
  });

  // Poll for result (same machinery the main generation pipeline uses)
  const pollUrl = `${MAGIC_FLOW_WEBHOOK_URL}/${generationId}/result`;
  const pollingResponse = await pollUntilCompleted({ url: pollUrl });
  const built = buildFlowMediaList({ generationId, pollingResponse });

  return { generationId, built };
};

export default submitAndPollGeneration;
